import moment from "moment-timezone";
import PropTypes from 'prop-types';
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { FormControl, InputGroup } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import useClientConfig from "../hooks/clientConfigHook";
import { cdate, parseCustomFieldDateToISO } from "../utils/CDate";
import { makeLabel } from "../utils/generalUtils";
import { DefaultButton } from './DefaultButton';
import { FloatingLabel } from "./FloatingLabel";
import Label from "./Label";
import { Validator } from "./ValidationResult";
import { withComponentCapabilities } from "./capabilities/componentCapabilities";
import { materializeValue } from "../utils/lazyValue";
import { Col, Row } from "./Grid";
import { parseDate } from "../utils/dateUtils";

const QueryControl = ({ def, value, minDate, maxDate, tz, href, onChange, placeholder, args, ...props }) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const startValue = parseCustomFieldDateToISO(value?.start, args?.tz ?? def?.tz ?? clientConfig?.tz);
    const endValue = parseCustomFieldDateToISO(value?.end, args?.tz ?? def?.tz ?? clientConfig?.tz);

    return <Row>
        <Col md><DateInput tz={args?.tz ?? def.tz ?? tz} value={startValue} placeholder={placeholder ? t('minPrefix', { label: placeholder}) : placeholder} minDate={args?.minDate ?? def?.minDate ?? minDate} maxDate={endValue ?? args?.maxDate ?? def?.maxDate ?? maxDate} href={href ?? def.href} onChange={(v) => onChange({start: v, end: value?.end})} {...props}></DateInput></Col>
        <Col md><DateInput tz={args?.tz ?? def.tz ?? tz} value={endValue} placeholder={placeholder ? t('maxPrefix', { label: placeholder}) : placeholder} minDate={startValue ?? args?.minDate ?? def?.minDate ?? minDate} maxDate={args?.maxDate ?? def?.maxDate ?? maxDate} href={href ?? def.href} onChange={(v) => onChange({start: value?.start, end: v})}{...props}></DateInput></Col>
    </Row>
}

export const customCtrlDef = {
    toString: async ({ value, defaultValue, def, tz, args, clientConfig }) => {
        value = await materializeValue(value);
        if (!value) {
            return defaultValue;
        }
        return cdate(value, args?.tz ?? def?.tz ?? tz ?? clientConfig?.tz).dateFormat(def?.showTz ?? clientConfig?.timezones?.enable, true);
    },
    def: (t, tools) => ({
        form: [{
            borderless: true,
            rows: [
                ...tools.labelFields(t),
                ...tools.fieldFields(t),
                // TODO minDate, maxDate, tz
                ...tools.commonFields(t)
            ]
        }]
    }),
    control: ({ def, value, minDate, maxDate, tz, href, ...props }, { clientConfig, args }) => {
        const dateValue = parseCustomFieldDateToISO(value, args?.tz ?? def?.tz ?? clientConfig?.tz);
        return <DateInput tz={args?.tz ?? def.tz ?? tz} value={dateValue} minDate={args?.minDate ?? def?.minDate ?? minDate} maxDate={args?.maxDate ?? def?.maxDate ?? maxDate} href={href ?? def.href} {...props}></DateInput>
    },
    queryControl: ({...props}, {args}) => {
        return <QueryControl args={args} {...props}></QueryControl>
    },
    comparator: (query, value) => {
        query = query?.value;
        value = value?.value;
        if (query === undefined) {
            return true;
        } else if (typeof query === 'string' && typeof value === 'string') {
            return query === value;
        } else if (typeof query === 'object' && typeof value == 'string') {
            let result = true;
            if (query.start) {
                result = result && value && parseDate(query.start) <= parseDate(value)
            }
            if (query.end) {
                result = result && value && parseDate(query.end) > parseDate(value);
            }
            return result;
        }
        return false;
    }
}

const CustomInput = forwardRef(({ value, dateFormat, required, onBack, onNext, onChange, onBlur, ...props}, parentRef) => {
    const { t } = useTranslation('common')
    const [validity, setValidity] = useState();
    const ref = useRef();
    const initialValue = useRef(value);
    const [dirty, setDirty] = useState(false);
    const [checkValue, setCheckValue] = useState(value);

    useImperativeHandle(parentRef, () => ref.current);

    useEffect(() => {
        setValidity();
        setCheckValue(_ => {
            if (value !== initialValue.current) {
                setDirty(true);
            } else {
                setDirty(false);
            }
            return value;
        })
    }, [value]);

    useEffect(() => {
        if (dirty) {
            const date = moment(checkValue, dateFormat.map(fmt => fmt.toUpperCase()), true);
            setValidity(oldValid => {
                if (checkValue == '') {
                    ref.current.setCustomValidity('');
                    ref.current.checkValidity();
                    return !required;
                } else {
                    const newValid = date.isValid();
                    ref.current.setCustomValidity(newValid ? '' : t('validationDateInvalid'))
                    ref.current.checkValidity();
                    return newValid;
                }
            });
        } else {
            setValidity();
        }
    }, [checkValue, dateFormat, t, required, dirty])
    const input = <FormControl className="d-flex w-fit" isInvalid={validity === false} value={value} type='text' required={required} onChange={onChange} onBlur={(...props) => { onChange(...props); onBlur(...props); }} onInvalid={() => setValidity(false)} {...props} ref={ref}></FormControl>
   
    if (onBack || onNext) {
        return <InputGroup>
            <Validator value={value} ref={ref} onValidation={setValidity}>
                {onBack ? <InputGroup.Prepend> <DefaultButton kind="previous" hideLabel onClick={onBack}/></InputGroup.Prepend> : null}
                {input}
                {onNext ? <InputGroup.Append> <DefaultButton kind="next" hideLabel onClick={onNext}/></InputGroup.Append> : null}
            </Validator>
        </InputGroup>}
            
    return <Validator value={value} ref={ref} onValidation={setValidity}>{input}</Validator>
})
CustomInput.propTypes = {
    value: PropTypes.string,
    dateFormat: PropTypes.any,
    required: PropTypes.bool,
    onBack: PropTypes.func,
    onNext: PropTypes.func
}

/**
 * An input tailored to displaying and accepting date values.
 * 
 * This control standardizes on its input value being an ISO timestamp string and its
 * output via the onChange function is also an ISO timestamp string.
 * @component
 */
const DateInput = withComponentCapabilities(({onBack: backInput, onNext: forwardInput, stepUnit, value, tz, placeholder, hideLabel = false, disabled = false, required = false, readonly = false, onChange, minDate: minDateInput, maxDate, filter, href}) => {
    const clientConfig = useClientConfig();
    const { i18n } = useTranslation();
    const [ guid ] = useState(crypto.randomUUID());
    const dateFormats = useMemo(() => i18n.resolvedLanguage === 'en' ? ['d-MMM-yyyy', 'd/MM/yyyy', 'dd/MM/yyyy', 'd/M/yyyy', 'd/MM/yy', 'd/M/yy', 'dd/MM/yy'] : undefined, [i18n])
    const minDate = useMemo(() => {
        if (minDateInput) {
            const min = cdate(minDateInput, tz);
            if (min.isValid()) {
                const dayStart = min.startOf('day');
                if (!min.isSame(dayStart)) {
                    return dayStart.toISOString();
                }
            }
        }
        return minDateInput;
    }, [minDateInput, tz]);
    
    // DatePickers only work in local tz, so we have to convert the times to counteract this
const displayValue = useMemo(() => !value || value instanceof Date ? value : moment.tz(value, tz ?? clientConfig.tz).tz(Intl.DateTimeFormat().resolvedOptions().timeZone, true).toDate(), [clientConfig.tz, tz, value]);
    const formattedMinDate = useMemo(() => !minDate || minDate instanceof Date ? minDate : moment.tz(minDate, tz ?? clientConfig.tz).tz(Intl.DateTimeFormat().resolvedOptions().timeZone, true).toDate(), [clientConfig.tz, minDate, tz]);
    const formattedMaxDate = useMemo(() => !maxDate || maxDate instanceof Date ? maxDate : moment.tz(maxDate, tz ?? clientConfig.tz).tz(Intl.DateTimeFormat().resolvedOptions().timeZone, true).toDate(), [clientConfig.tz, maxDate, tz]);
    
    const handleChange = newValue => {
        const date = cdate(newValue).tz(tz ?? clientConfig.tz, true);
        if (date.isValid()) {
            let iso = date.toISOString();
            if (minDateInput) {
                const min = cdate(minDateInput, tz);
                if (min.isValid() && date.isBefore(min) && date.isSame(min, 'day')) {
                    iso = min.toISOString();
                }
            }
            if (value !== iso) {
                onChange(iso);
            }
        } else if (value !== null) {
            onChange(null);
        }
    }
    if ((backInput || forwardInput) && stepUnit) {
        throw new Error("Cannot specify both onBack or onNext with stepUnits");
    }

    let onBack;
    if (stepUnit) {
        onBack = () => handleChange(cdate(value).add(-1, stepUnit).toISOString());
    } else if (backInput){
        onBack = backInput;
    }
    
    let onNext;

    if (stepUnit) {
        onNext = () => handleChange(cdate(value).add(1, stepUnit).toISOString());
    } else if (forwardInput) {
        onNext = forwardInput;
    }

    if (readonly) {
        const format = i18n.resolvedLanguage === 'en' ? 'D-MMM-YYYY' : 'L';
        return <Label placeholder={placeholder} hideLabel={hideLabel} text={!value ? '\u00A0' : moment.tz(value, tz ?? clientConfig.tz).format(format)} href={href}></Label>;
    }    

    return <FloatingLabel hideLabel={hideLabel} placeholder={placeholder} required={required} value={displayValue} href={href}>
        <DatePicker
        customInput={<CustomInput dateFormat={dateFormats} onBack={onBack} onNext={onNext}></CustomInput>}
        selected={displayValue}
        placeholderText={makeLabel(placeholder, required)}
        disabled={disabled}
        required={required}
        locale={i18n.resolvedLanguage}
        showMonthDropdown
        showYearDropdown
        scrollableYearDropdown
        yearDropdownItemNumber={100}
        className="form-control"
        portalId={guid}
        dateFormat={dateFormats}
        onChange={handleChange}
        minDate={formattedMinDate}
        maxDate={formattedMaxDate}
        filterDate={filter}
        ></DatePicker>
    </FloatingLabel>
}, {

});

DateInput.propTypes = {
    /**
     * The date to be displayed by the control as an ISO string
     */
    value: PropTypes.string,

    /**
     * The timezone in which to render the date, defaults to clientConfig.tz.
     * Timezone is expressed as a TZ identifier like Australia/Brisbane or Europe/London
     */
    tz: PropTypes.string,

    /**
     * Function to call when the value has been changed
     */
    onChange: PropTypes.func,

    /**
     * The label for the control
     */
    placeholder: PropTypes.string,

    /**
     * The control must have a value for the from in which it is contained to validate
     */
    required: PropTypes.bool,

    /**
     * Force the input to be read-only
     */
    disabled: PropTypes.bool,

    /**
     * Collapses the floating label used for spacing
     */
    hideLabel: PropTypes.bool,

    /**
     * Render the control as inline text rather than as an input control
     */
    readonly: PropTypes.bool,

    /**
     * Override Function that adjusts the given date by subtracting from date by given unit 
     */
    onBack: PropTypes.func,

    /**
     * Override Function that adjusts the given date by adding to date by given unit 
     */
    onNext: PropTypes.func,

    /**
     * Unit in wich date will be altered in onBack and onNext
     */
    stepUnit: PropTypes.string,

    /**
     * The minimum date to be selectable by the control as an ISO string
     */
    minDate: PropTypes.string,

    /**
     * The maximun date to be selectable by the control as an ISO string
     */
    maxDate: PropTypes.string,
    /**
    * An optional function to filter selectable dates. 
    * The function should return true for dates that should be selectable.
    * Example: Only allow Mondays: `(date) => date.getDay() === 1`.
    */
    filter: PropTypes.func,

    /**
     * An option navigation URL to be followed if the label text is clicked
     */
    href: PropTypes.string,
}

export default DateInput;